import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react'
import { Modal } from 'antd'
import { useTranslation } from 'react-i18next'
import { GridApi } from 'ag-grid-enterprise'
import AmendmentModal from '../../../amendment/components/AmendmentModal'
import InvestmentModal from '../../../investment/components/InvestmentModal'
import { DeprecationModal } from '../../../deprecationPlan/components/DeprecationModal'
import BudgetMethodModal from '../../../budgetingMethod/components/BudgetMethodModal'
import { CommentModal } from '../../../budgetComments/components/Comments'
import { VoucherModal } from '../../../vouchers/components/VoucherModal'
import { EnhancedStatementRow, FinancialRowType } from '../../utils/dataEnhancement'
import { StatementColumn } from '../../types'
import { ReportTableRow } from '../../../../components/Table/types'
import { Investment } from '../../../../types/investment/Investment'
import { useDeleteInvestment } from '../../../investment/hooks/useDeleteInvestment'
import { useDeleteAmendment } from '../../../amendment/hooks/useDeleteAmendment'

// --- Types for discriminated union modal state ---
export type InvestmentModalPayload = {
  type: 'investment'
  data: EnhancedStatementRow
  id?: number
}

export type DivestmentModalPayload = {
  type: 'divestment'
  data: EnhancedStatementRow
  id?: number
}

export type DeprecationModalPayload = {
  type: 'deprecation'
  data: EnhancedStatementRow
}

export type AmendmentModalPayload = {
  type: 'amendment'
  data: EnhancedStatementRow
  id?: number
}

export type BudgetMethodModalPayload = {
  type: 'budgetMethod'
  data: EnhancedStatementRow
}

export type CommentModalPayload = {
  type: 'comment'
  row: EnhancedStatementRow
  column: StatementColumn
}

export type VoucherModalPayload = {
  type: 'voucher'
  row: EnhancedStatementRow
  column: StatementColumn
}

export type ModalState =
  | InvestmentModalPayload
  | DivestmentModalPayload
  | DeprecationModalPayload
  | AmendmentModalPayload
  | BudgetMethodModalPayload
  | CommentModalPayload
  | VoucherModalPayload
  | { type: null }

// --- Modal actions context (only context needed) ---
type ModalActionsContextValue = {
  openInvestmentModal: (payload: Omit<InvestmentModalPayload, 'type'>) => void
  openDivestmentModal: (payload: Omit<DivestmentModalPayload, 'type'>) => void
  openDeprecationModal: (payload: Omit<DeprecationModalPayload, 'type'>) => void
  openAmendmentModal: (payload: Omit<AmendmentModalPayload, 'type'>) => void
  openBudgetMethodModal: (payload: Omit<BudgetMethodModalPayload, 'type'>) => void
  openCommentModal: (payload: Omit<CommentModalPayload, 'type'>) => void
  openVoucherModal: (payload: Omit<VoucherModalPayload, 'type'>) => void
  openEdit: (data: EnhancedStatementRow) => void
  openDelete: (data: EnhancedStatementRow) => void
  closeModal: () => void
  // Optional gridApi for budget method modal success callback
  setGridApi?: (api: GridApi<EnhancedStatementRow> | undefined) => void
}

const ModalActionsContext = createContext<ModalActionsContextValue | undefined>(undefined)

// --- Provider component ---
export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation()
  // modalState is internal - only used to determine which modal to render
  const [modalState, setModalState] = useState<ModalState>({ type: null })
  const [gridApi, setGridApi] = useState<GridApi<EnhancedStatementRow>>()

  // Delete mutations
  const { mutateAsync: deleteInvestment } = useDeleteInvestment(false) // isExisting = false for Balance Sheet
  const { mutateAsync: deleteAmendment } = useDeleteAmendment()

  const openInvestmentModal = useCallback((payload: Omit<InvestmentModalPayload, 'type'>) => {
    setModalState({ type: 'investment', ...payload })
  }, [])

  const openDivestmentModal = useCallback((payload: Omit<InvestmentModalPayload, 'type'>) => {
    setModalState({ type: 'divestment', ...payload })
  }, [])

  const openDeprecationModal = useCallback((payload: Omit<DeprecationModalPayload, 'type'>) => {
    setModalState({ type: 'deprecation', ...payload })
  }, [])

  const openAmendmentModal = useCallback((payload: Omit<AmendmentModalPayload, 'type'>) => {
    setModalState({ type: 'amendment', ...payload })
  }, [])

  const openBudgetMethodModal = useCallback((payload: Omit<BudgetMethodModalPayload, 'type'>) => {
    setModalState({ type: 'budgetMethod', ...payload })
  }, [])

  const openCommentModal = useCallback((payload: Omit<CommentModalPayload, 'type'>) => {
    setModalState({ type: 'comment', ...payload })
  }, [])

  const openVoucherModal = useCallback((payload: Omit<VoucherModalPayload, 'type'>) => {
    setModalState({ type: 'voucher', ...payload })
  }, [])

  const openEdit = useCallback((data: EnhancedStatementRow) => {
    switch (data.rowType) {
      case FinancialRowType.INVESTMENT:
        openInvestmentModal({ data, id: data.id })
        break
      case FinancialRowType.AMENDMENT:
        openAmendmentModal({ data, id: data.id })
        break

      default:
        break
    }
  }, [])

  const openDelete = useCallback(
    (data: EnhancedStatementRow) => {
      if (!data.id || !data._sourceAccountCode) {
        console.error('Cannot delete: missing ID or account code')
        return
      }

      // Determine delete handler based on row type
      const handleDelete = async () => {
        try {
          if (data.rowType === FinancialRowType.INVESTMENT) {
            await deleteInvestment({
              id: data.id!,
              accountCode: data._sourceAccountCode!
            } as Investment)
          } else if (data.rowType === FinancialRowType.AMENDMENT) {
            await deleteAmendment({
              id: data.id!,
              accountCode: data._sourceAccountCode!
            })
          } else {
            console.warn('Delete not supported for row type:', data.rowType)
          }
          // Grid will update automatically via observer when query invalidates
        } catch (err) {
          console.error(`Error deleting ${data.rowType}:`, err)
        }
      }

      // Single modal for all delete operations
      Modal.confirm({
        title: t('modal:deleteTitle', 'Haluatko varmasti poistaa?'),
        content: `${data.name}`,
        okText: t('global:delete'),
        okType: 'danger',
        cancelText: t('global:cancel'),
        onOk: handleDelete
      })
    },
    [t, deleteInvestment, deleteAmendment]
  )

  const closeModal = useCallback(() => setModalState({ type: null }), [])

  // Helper used specifically when InvestmentModal's `onFinish` needs to behave slightly differently
  const handleCloseInvestmentModal = useCallback(() => {
    // You can add cleanup logic here if needed (e.g. reset form state, refetch, etc.)
    closeModal()
  }, [closeModal])

  // Budget method modal success handler
  const handleBudgetMethodSuccess = useCallback(() => {
    // Refresh the grid to show updated budgeting method
    if (gridApi) {
      gridApi.refreshCells({ force: true })
    }
  }, [gridApi])

  // Memoize actions - this object NEVER changes (all callbacks are stable via useCallback)
  const actions: ModalActionsContextValue = useMemo(
    () => ({
      openInvestmentModal,
      openDivestmentModal,
      openDeprecationModal,
      openAmendmentModal,
      openBudgetMethodModal,
      openCommentModal,
      openVoucherModal,
      openEdit,
      openDelete,
      closeModal,
      setGridApi
    }),
    [
      openInvestmentModal,
      openDivestmentModal,
      openDeprecationModal,
      openAmendmentModal,
      openBudgetMethodModal,
      openCommentModal,
      openVoucherModal,
      openEdit,
      openDelete,
      closeModal
    ]
  )

  return (
    <ModalActionsContext.Provider value={actions}>
      {children}

      {/* Render modals here so they are controlled by context and mounted at the app root */}
      {modalState.type === 'investment' && (
        <InvestmentModal
          sourceDisabled
          visible
          isExisting={false} // Balance Sheet investments are always new investments, not existing fixed assets
          formType={modalState.id ? undefined : 'investment'}
          reportTableRow={modalState.data as unknown as ReportTableRow}
          id={modalState.id}
          onFinish={handleCloseInvestmentModal}
        />
      )}

      {modalState.type === 'divestment' && (
        <InvestmentModal
          sourceDisabled
          visible
          isExisting={false} // Balance Sheet investments are always new investments, not existing fixed assets
          formType={modalState.id ? undefined : 'divestment'}
          id={modalState.id}
          reportTableRow={modalState.data as unknown as ReportTableRow}
          onFinish={handleCloseInvestmentModal}
        />
      )}

      {modalState.type === 'deprecation' && <DeprecationModal visible rowData={modalState.data} onClose={closeModal} />}

      {modalState.type === 'amendment' && (
        <AmendmentModal visible rowData={modalState.data} amendmentId={modalState.id} onClose={closeModal} />
      )}

      {modalState.type === 'budgetMethod' && (
        <BudgetMethodModal visible row={modalState.data} onClose={closeModal} onSuccess={handleBudgetMethodSuccess} />
      )}

      {modalState.type === 'comment' && (
        <CommentModal open row={modalState.row} column={modalState.column} onClose={closeModal} />
      )}

      {modalState.type === 'voucher' && (
        <VoucherModal row={modalState.row} column={modalState.column} open onClose={closeModal} />
      )}
    </ModalActionsContext.Provider>
  )
}

// --- Hook for consumers ---

/**
 * Hook to access modal actions (never causes re-renders)
 * Use this to open/close modals from any component
 */
export const useModalActions = (): ModalActionsContextValue => {
  const ctx = useContext(ModalActionsContext)
  if (!ctx) throw new Error('useModalActions must be used within a ModalProvider')
  return ctx
}

/**
 * Legacy alias for backwards compatibility
 * @deprecated Use useModalActions() instead
 */
export const useModal = useModalActions

export default useModalActions
